import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';

const RoadmapPrestataireModal = ({ open, onClose, devisId, onSuccess }) => {
  const [devis, setDevis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prestataires, setPrestataires] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [prestatairesByBlock, setPrestatairesByBlock] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && devisId) {
      setLoading(true);
      loadData();
    }
  }, [open, devisId]);

  const loadData = async () => {
    try {
      const [devisRes, prestRes, rolesRes, roadmapsRes] = await Promise.all([
        axios.get(`${API}/devis/${devisId}`),
        axios.get(`${API}/prestataires`, { withCredentials: true }),
        axios.get(`${API}/prestataire-roles`),
        axios.get(`${API}/roadmaps`, {
          withCredentials: true,
          params: { devis_id: devisId }
        })
      ]);
      setDevis(devisRes.data);
      setPrestataires(prestRes.data);
      setRoleOptions(rolesRes.data);

      const blocks = extractBlocks(devisRes.data);
      const existingRoadmap = (roadmapsRes.data || [])[0];
      const initial = buildInitialPrestatairesByBlock(blocks, existingRoadmap?.blocks || []);
      setPrestatairesByBlock(initial);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const extractBlocks = (devis) => {
    if (!devis?.dates) return [];
    const blocks = [];
    const uniqueDates = {};
    
    devis.dates.forEach(dateInfo => {
      const dateStr = dateInfo.date;
      if (!uniqueDates[dateStr]) {
        uniqueDates[dateStr] = [];
      }
      uniqueDates[dateStr].push(dateInfo);
    });

    Object.entries(uniqueDates).forEach(([date, events]) => {
      events.forEach(event => {
        blocks.push({
          title: event.type || 'Événement',
          date,
          heure: event.heure,
          lieu: event.lieu
        });
      });
    });

    return blocks;
  };

  const getBlockKey = (block) => {
    const title = (block.title || '').trim().toLowerCase();
    const date = (block.date || '').trim();
    const heure = (block.heure || '').trim();
    const lieu = (block.lieu || '').trim().toLowerCase();
    return `${date}__${title}__${heure}__${lieu}`;
  };

  const getRelaxedBlockKey = (block) => {
    const title = (block.title || '').trim().toLowerCase();
    const date = (block.date || '').trim();
    return `${date}__${title}`;
  };

  const getDateOnlyKey = (block) => {
    return (block.date || '').trim();
  };

  const buildInitialPrestatairesByBlock = (devisBlocks, roadmapBlocks) => {
    const roadmapWithIndex = (roadmapBlocks || []).map((block, index) => ({ block, index }));
    const usedRoadmapIndexes = new Set();

    const exactMap = new Map();
    const relaxedMap = new Map();
    const dateOnlyMap = new Map();

    roadmapWithIndex.forEach(({ block, index }) => {
      const exact = getBlockKey(block);
      const relaxed = getRelaxedBlockKey(block);
      const dateOnly = getDateOnlyKey(block);

      if (!exactMap.has(exact)) exactMap.set(exact, []);
      if (!relaxedMap.has(relaxed)) relaxedMap.set(relaxed, []);
      if (!dateOnlyMap.has(dateOnly)) dateOnlyMap.set(dateOnly, []);

      exactMap.get(exact).push({ block, index });
      relaxedMap.get(relaxed).push({ block, index });
      dateOnlyMap.get(dateOnly).push({ block, index });
    });

    const pickUnused = (candidates = []) => {
      for (const candidate of candidates) {
        if (!usedRoadmapIndexes.has(candidate.index)) {
          usedRoadmapIndexes.add(candidate.index);
          return candidate.block;
        }
      }
      return null;
    };

    const initial = {};
    devisBlocks.forEach((devisBlock, index) => {
      const exactMatch = pickUnused(exactMap.get(getBlockKey(devisBlock)));
      const relaxedMatch = exactMatch ? null : pickUnused(relaxedMap.get(getRelaxedBlockKey(devisBlock)));
      const dateOnlyMatch = exactMatch || relaxedMatch ? null : pickUnused(dateOnlyMap.get(getDateOnlyKey(devisBlock)));

      // Last fallback: keep old behavior by position if still available.
      let positionalMatch = null;
      if (!exactMatch && !relaxedMatch && !dateOnlyMatch && roadmapBlocks[index]) {
        const alreadyUsed = usedRoadmapIndexes.has(index);
        if (!alreadyUsed) {
          usedRoadmapIndexes.add(index);
          positionalMatch = roadmapBlocks[index];
        }
      }

      const matchedBlock = exactMatch || relaxedMatch || dateOnlyMatch || positionalMatch;
      const matchedPrestataires = matchedBlock?.prestataires || [];

      initial[index] = matchedPrestataires.map((p) => ({
        role: p.role || '',
        name: p.name || '',
        prestataire_id: p.prestataire_id || '',
      }));
    });
    return initial;
  };

  const addPrestataireToBlock = (blockIndex) => {
    setPrestatairesByBlock(prev => ({
      ...prev,
      [blockIndex]: [...(prev[blockIndex] || []), { role: '', name: '', prestataire_id: '' }]
    }));
  };

  const removePrestataireFromBlock = (blockIndex, prestIndex) => {
    setPrestatairesByBlock(prev => ({
      ...prev,
      [blockIndex]: prev[blockIndex].filter((_, i) => i !== prestIndex)
    }));
  };

  const updatePrestataire = (blockIndex, prestIndex, field, value) => {
    setPrestatairesByBlock(prev => {
      const updated = [...prev[blockIndex]];
      updated[prestIndex] = { ...updated[prestIndex], [field]: value };
      
      // If prestataire_id is selected, auto-fill name
      if (field === 'prestataire_id' && value) {
        const presta = prestataires.find(p => p.id === value);
        if (presta) {
          updated[prestIndex].name = `${presta.prenom} ${presta.nom}`;
        }
      }
      
      return { ...prev, [blockIndex]: updated };
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/roadmaps/from-devis/${devisId}`,
        { prestataires_by_block: prestatairesByBlock },
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );
      toast.success('Feuille de route créée avec succès');
      onSuccess(res.data);
      onClose();
    } catch (error) {
      // Améliorer l'affichage des erreurs
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        const errorMessages = [];
        
        if (errors.event_title) {
          errorMessages.push('• Titre de l\'événement manquant');
        }
        if (errors.event_date) {
          errorMessages.push('• Date de l\'événement manquante');
        }
        if (errors.blocks) {
          errorMessages.push('• Aucune date trouvée dans le devis');
        }
        
        if (errorMessages.length > 0) {
          const errorText = [
            'Impossible de créer la feuille de route :',
            ...errorMessages,
            '',
            'Veuillez compléter les informations de l\'événement dans le devis avant de générer la feuille de route.'
          ].join('\n');
          
          toast.error(errorText, { duration: 8000 });
        } else {
          toast.error('Erreur lors de la création de la feuille de route');
        }
      } else {
        toast.error('Erreur lors de la création de la feuille de route');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const blocks = devis ? extractBlocks(devis) : [];
  const hasNoDates = !loading && blocks.length === 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Assigner des prestataires à la feuille de route</DialogTitle>
          <DialogDescription>
            Définissez les prestataires pour chaque événement avant de générer la feuille de route.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center">Chargement...</div>
        ) : hasNoDates ? (
          <div className="py-8 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="font-semibold text-amber-800 mb-2">⚠️ Informations manquantes</h3>
              <p className="text-sm text-amber-700 mb-3">
                Ce devis ne contient aucune date d'événement. Pour générer une feuille de route, vous devez :
              </p>
              <ul className="list-disc list-inside text-sm text-amber-700 space-y-1 mb-3">
                <li>Ajouter au moins une date dans les informations de l'événement du devis</li>
                <li>Compléter le titre de l'événement si nécessaire</li>
              </ul>
              <p className="text-xs text-amber-600">
                Veuillez modifier le devis pour ajouter ces informations, puis réessayez de générer la feuille de route.
              </p>
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={onClose}>
                Fermer
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {blocks.map((block, blockIndex) => (
              <div key={blockIndex} className="border rounded-lg p-4 bg-stone-50">
                <div className="mb-3">
                  <h3 className="font-semibold text-stone-800">{block.title}</h3>
                  <p className="text-sm text-stone-600">
                    {new Date(block.date).toLocaleDateString('fr-FR')} à {block.heure} - {block.lieu}
                  </p>
                </div>

                <div className="space-y-2">
                  {(prestatairesByBlock[blockIndex] || []).map((presta, prestIndex) => (
                    <div key={prestIndex} className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-4">
                        <Label className="text-xs">Rôle</Label>
                        <Select
                          value={presta.role}
                          onValueChange={(val) => updatePrestataire(blockIndex, prestIndex, 'role', val)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            {roleOptions.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="col-span-4">
                        <Label className="text-xs">Prestataire (optionnel)</Label>
                        <Select
                          value={presta.prestataire_id}
                          onValueChange={(val) => updatePrestataire(blockIndex, prestIndex, 'prestataire_id', val)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Choisir..." />
                          </SelectTrigger>
                          <SelectContent>
                            {prestataires.map(p => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.prenom} {p.nom}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="col-span-3">
                        <Label className="text-xs">Nom personnalisé</Label>
                        <Input
                          value={presta.name}
                          onChange={(e) => updatePrestataire(blockIndex, prestIndex, 'name', e.target.value)}
                          placeholder="Nom"
                          className="h-9"
                        />
                      </div>

                      <div className="col-span-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removePrestataireFromBlock(blockIndex, prestIndex)}
                          className="h-9 w-9 p-0"
                        >
                          <Trash2 className="h-4 w-4 text-rose-500" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addPrestataireToBlock(blockIndex)}
                    className="mt-2"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter un prestataire
                  </Button>
                </div>
              </div>
            ))}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={onClose} disabled={submitting}>
                Annuler
              </Button>
              <Button onClick={handleSubmit} disabled={submitting} className="bg-amber-600 hover:bg-amber-700">
                {submitting ? 'Création...' : 'Créer la feuille de route'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RoadmapPrestataireModal;
